import React, { useEffect, useMemo, useState } from 'react';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import { Switch } from '@this/shared/ui/inputs/switch';
import type {
  PreferredTransportType,
  ProposalBundle,
  ProposalItem,
  TicketCategory
} from '../../api/proposalBundles';
import { formatProposalPrice } from '../../utils/priceFormatter';
import { formatTransportRouteInfo } from '../../utils/routeInfoFormatter';

interface Props {
  bundle: ProposalBundle;
  productNumber: number;
  includeTax: boolean;
  onSelect?: (bundleId: number, itemId: number) => void;
  onClose?: (bundleId: number) => void;
  disabled?: boolean;
}

const CATEGORY_LABELS_BY_TYPE = {
  rail: {
    outward: '往路新幹線',
    homeward: '復路新幹線',
    transport: '新幹線',
    default: '新幹線'
  },
  air: {
    outward: '往路航空券',
    homeward: '復路航空券',
    transport: '交通機関',
    default: '交通機関'
  }
} as const;

const PREFERRED_TRANSPORT_TYPE = {
  rail: 'rail',
  air: 'air'
} as const;

const isRailPreferred = (type: PreferredTransportType | null): boolean => {
  return type === PREFERRED_TRANSPORT_TYPE.rail;
};

const isAirPreferred = (type: PreferredTransportType | null): boolean => {
  return type === PREFERRED_TRANSPORT_TYPE.air;
};

const getCategoryLabel = (category: string, preferredType: PreferredTransportType | null): string => {
  const type = isRailPreferred(preferredType) ? PREFERRED_TRANSPORT_TYPE.rail : PREFERRED_TRANSPORT_TYPE.air;
  const labels = CATEGORY_LABELS_BY_TYPE[type];
  return (labels as Record<string, string>)[category] ?? labels.default;
};

const getCategoryIcon = (preferredType: PreferredTransportType | null): React.ReactNode => {
  if (isRailPreferred(preferredType)) {
    return <img src="/images/train.png" alt="train" style={{ width: '16px', height: '16px' }} />;
  }

  return <img src="/images/plane.png" alt="flight" style={{ width: '16px', height: '16px' }} />;
};

const filterItemsByTicketCategory = (items: ProposalItem[], category: TicketCategory): ProposalItem[] => {
  return items.filter(item => item.ticketCategory === category);
};

const hasItemsForCategory = (items: ProposalItem[], category: TicketCategory): boolean => {
  return items.some(item => item.ticketCategory === category);
};

const filterItemsByPreferredType = (
  items: ProposalItem[],
  preferredType: PreferredTransportType | null
): ProposalItem[] => {
  if (isRailPreferred(preferredType)) {
    return items.filter(item => item.isShinkansen === true);
  }

  if (preferredType === PREFERRED_TRANSPORT_TYPE.air) {
    return items.filter(item => item.isAir === true);
  }

  return items;
};

const TransportCard = ({ bundle, productNumber, includeTax, onSelect, onClose, disabled = false }: Props) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);

  const preferredTransportType = bundle.preferredTransportType;
  const effectivePreferredType = preferredTransportType ?? PREFERRED_TRANSPORT_TYPE.rail;
  const [selectedTransportType, setSelectedTransportType] =
    useState<PreferredTransportType>(effectivePreferredType);

  useEffect(() => {
    setSelectedTransportType(effectivePreferredType);
  }, [effectivePreferredType]);

  const hasRailItems = useMemo(() => bundle.items.some(item => item.isShinkansen === true), [bundle.items]);
  const hasAirItems = useMemo(() => bundle.items.some(item => item.isAir === true), [bundle.items]);
  const showTransportTypeToggle = hasRailItems && hasAirItems;
  const displayTransportType = showTransportTypeToggle
    ? selectedTransportType
    : hasRailItems
    ? PREFERRED_TRANSPORT_TYPE.rail
    : PREFERRED_TRANSPORT_TYPE.air;

  const transportItems = useMemo(
    () => filterItemsByPreferredType(bundle.items, displayTransportType),
    [bundle.items, displayTransportType]
  );
  const hasChangeable = useMemo(() => hasItemsForCategory(transportItems, 'changeable'), [transportItems]);
  const hasUnchangeable = useMemo(() => hasItemsForCategory(transportItems, 'unchangeable'), [transportItems]);

  const defaultCategory: TicketCategory = hasChangeable ? 'changeable' : 'unchangeable';
  const [selectedTicketCategory, setSelectedTicketCategory] = useState<TicketCategory>(defaultCategory);

  useEffect(() => {
    if (isAirPreferred(displayTransportType) === false) return;

    if (selectedTicketCategory === 'changeable' && hasChangeable === false && hasUnchangeable) {
      setSelectedTicketCategory('unchangeable');
      return;
    }

    if (selectedTicketCategory === 'unchangeable' && hasUnchangeable === false && hasChangeable) {
      setSelectedTicketCategory('changeable');
    }
  }, [displayTransportType, hasChangeable, hasUnchangeable, selectedTicketCategory]);

  const filteredItems = useMemo(
    () =>
      isRailPreferred(displayTransportType)
        ? transportItems
        : filterItemsByTicketCategory(transportItems, selectedTicketCategory),
    [displayTransportType, transportItems, selectedTicketCategory]
  );

  const categoryLabel = getCategoryLabel(bundle.category, displayTransportType);
  const categoryIcon = getCategoryIcon(displayTransportType);
  const routeInfo = useMemo(
    () => formatTransportRouteInfo(bundle.origin, bundle.destination, bundle.departureDate),
    [bundle.origin, bundle.destination, bundle.departureDate]
  );

  const handleSelect = (itemId: number) => {
    onSelect?.(bundle.id, itemId);
  };

  const handleClose = () => {
    onClose?.(bundle.id);
  };

  const handleTicketCategoryChange = (category: TicketCategory) => {
    setSelectedTicketCategory(category);
  };

  const showTicketCategorySelector =
    displayTransportType === PREFERRED_TRANSPORT_TYPE.air && hasChangeable && hasUnchangeable;

  return (
    <div style={styles.card}>
      <button
        type="button"
        style={{
          ...styles.closeButton,
          ...(disabled ? { opacity: 0.5, cursor: 'not-allowed' } : {})
        }}
        onClick={handleClose}
        disabled={disabled}
      >
        ×
      </button>

      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <span style={styles.productBadge}>商品{productNumber}</span>
          <span style={styles.categoryLabel}>
            <span style={styles.categoryIcon}>{categoryIcon}</span>
            <span>{categoryLabel}</span>
            {routeInfo}
          </span>
        </div>
      </div>

      {showTicketCategorySelector && (
        <TicketCategorySelector
          bundleId={bundle.id}
          selectedCategory={selectedTicketCategory}
          onChange={handleTicketCategoryChange}
          theme={theme}
        />
      )}

      <table style={styles.table}>
        <thead>
          <tr style={styles.tableHeaderRow}>
            <th style={styles.tableHeaderCell}>時間</th>
            <th style={styles.tableHeaderCell}>便名・クラス</th>
            <th style={styles.tableHeaderCell}>料金</th>
          </tr>
        </thead>
        <tbody>
          {filteredItems.map((item, index) => (
            <TransportRow
              key={item.id}
              item={item}
              index={index}
              includeTax={includeTax}
              onSelect={() => handleSelect(item.id)}
              theme={theme}
              disabled={disabled}
            />
          ))}
        </tbody>
      </table>

      {showTransportTypeToggle && (
        <div style={styles.switchFooter}>
          <TransportTypeToggle
            value={selectedTransportType}
            onChange={setSelectedTransportType}
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
};

interface TicketCategorySelectorProps {
  bundleId: number;
  selectedCategory: TicketCategory;
  onChange: (category: TicketCategory) => void;
  theme: AITTheme;
}

const TicketCategorySelector = ({ bundleId, selectedCategory, onChange, theme }: TicketCategorySelectorProps) => {
  const styles = useMemo(() => getTicketCategorySelectorStyles(theme), [theme]);
  const radioName = `ticketCategory-${bundleId}`;

  return (
    <div style={styles.container}>
      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
      <label style={styles.label}>
        <input
          type="radio"
          name={radioName}
          value="changeable"
          checked={selectedCategory === 'changeable'}
          onChange={() => onChange('changeable')}
          style={styles.radio}
        />
        <span style={selectedCategory === 'changeable' ? styles.labelTextActive : styles.labelText}>
          変更可（正規運賃）
        </span>
      </label>
      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
      <label style={styles.label}>
        <input
          type="radio"
          name={radioName}
          value="unchangeable"
          checked={selectedCategory === 'unchangeable'}
          onChange={() => onChange('unchangeable')}
          style={styles.radio}
        />
        <span style={selectedCategory === 'unchangeable' ? styles.labelTextActive : styles.labelText}>
          変更不可（割引運賃）
        </span>
      </label>
    </div>
  );
};

interface TransportTypeToggleProps {
  value: PreferredTransportType;
  onChange: (value: PreferredTransportType) => void;
  disabled?: boolean;
}

const TransportTypeToggle = ({ value, onChange, disabled = false }: TransportTypeToggleProps) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getTransportTypeToggleStyles(theme), [theme]);

  const handleToggle = (checked: boolean) => {
    onChange(checked ? PREFERRED_TRANSPORT_TYPE.air : PREFERRED_TRANSPORT_TYPE.rail);
  };
  const railActive = isRailPreferred(value);
  const airActive = isAirPreferred(value);

  return (
    <div style={styles.container}>
      <span style={railActive ? styles.iconActive : styles.icon}>
        <img src="/images/train.png" alt="train" style={styles.iconImage} />
      </span>
      <Switch checked={airActive} onChange={handleToggle} disabled={disabled} />
      <span style={airActive ? styles.iconActive : styles.icon}>
        <img src="/images/plane.png" alt="flight" style={styles.iconImage} />
      </span>
    </div>
  );
};

interface TransportRowProps {
  item: ProposalItem;
  index: number;
  includeTax: boolean;
  onSelect: () => void;
  theme: AITTheme;
  disabled?: boolean;
}

const TransportRow = ({ item, index, includeTax, onSelect, theme, disabled = false }: TransportRowProps) => {
  const styles = useMemo(
    () => getRowStyles(theme, item.selected ?? false, disabled),
    [theme, item.selected, disabled]
  );
  const circleNumber = ['①', '②', '③', '④', '⑤'][index] || `${index + 1}`;

  const timeDisplay = item.departureTime && item.arrivalTime ? `${item.departureTime}-${item.arrivalTime}` : '-';

  const flightDisplay = item.flightName || item.title || '-';
  const seatDisplay = item.seatClass ? `（${item.seatClass}）` : '';
  const ticketBadge = item.ticketType || null;
  const priceDisplay = formatProposalPrice({
    price: item.price,
    priceWithTax: item.priceWithTax,
    includeTax
  });

  const handleClick = () => {
    const selectable = (item.selected ?? false) === false && disabled === false;
    if (selectable) onSelect();
  };

  return (
    <tr style={styles.row}>
      <td style={styles.cellTime}>
        <div style={styles.timeContainer}>
          <div style={styles.numberCircle}>{circleNumber}</div>
          <span>{timeDisplay}</span>
        </div>
      </td>
      <td style={styles.cell}>
        <strong>{flightDisplay}</strong>
        {seatDisplay}
        {ticketBadge && (
          <>
            <br />
            <span style={styles.ticketBadge}>{ticketBadge}</span>
          </>
        )}
      </td>
      <td style={styles.cellPrice}>
        <div style={styles.priceContainer}>
          <span>{priceDisplay}</span>
          <button
            type="button"
            style={styles.selectButton}
            onClick={handleClick}
            disabled={item.selected || disabled}
          >
            選択
          </button>
        </div>
      </td>
    </tr>
  );
};

const getStyles = (theme: AITTheme) => ({
  card: {
    backgroundColor: theme.whiteColor,
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '20px',
    border: `2px solid ${theme.linkColor}`,
    position: 'relative' as const
  },
  closeButton: {
    position: 'absolute' as const,
    top: '12px',
    right: '12px',
    backgroundColor: '#f5f5f5',
    color: '#666',
    border: 'none',
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    fontSize: '18px',
    fontWeight: 300 as const,
    lineHeight: 1,
    padding: 0,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  header: {
    fontWeight: 700 as const,
    color: theme.linkColor,
    marginBottom: '16px',
    fontSize: '17px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px'
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  productBadge: {
    backgroundColor: theme.linkColor,
    color: '#ffffff',
    padding: '4px 12px',
    borderRadius: '16px',
    fontSize: '11px',
    fontWeight: 600 as const
  },
  categoryLabel: {
    fontSize: '14px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px'
  },
  categoryIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1
  },
  switchFooter: {
    marginTop: '12px',
    display: 'flex',
    justifyContent: 'flex-end'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const
  },
  tableHeaderRow: {
    backgroundColor: '#f5f5f5',
    borderBottom: '2px solid #ececec'
  },
  tableHeaderCell: {
    padding: '10px',
    textAlign: 'center' as const,
    fontSize: '13px'
  },
  tableHeaderCellRight: {
    padding: '10px',
    textAlign: 'center' as const,
    fontSize: '13px'
  },
  tableHeaderCellCenter: {
    padding: '10px',
    textAlign: 'center' as const,
    fontSize: '13px',
    width: '110px'
  }
});

const getTicketCategorySelectorStyles = (theme: AITTheme) => ({
  container: {
    display: 'flex',
    gap: '24px',
    marginBottom: '16px',
    padding: '12px 16px',
    backgroundColor: '#f8f9fa',
    borderRadius: '6px'
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer'
  },
  radio: {
    width: '18px',
    height: '18px',
    accentColor: theme.linkColor,
    cursor: 'pointer'
  },
  labelText: {
    fontSize: '14px',
    color: theme.textColor
  },
  labelTextActive: {
    fontSize: '14px',
    color: theme.linkColor,
    fontWeight: 600 as const
  }
});

const getTransportTypeToggleStyles = (theme: AITTheme) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '12px',
    color: theme.textColor
  },
  icon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '16px',
    height: '16px',
    opacity: 0.5
  },
  iconActive: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '16px',
    height: '16px',
    opacity: 1
  },
  iconImage: {
    width: '16px',
    height: '16px'
  }
});

const getRowStyles = (theme: AITTheme, selected: boolean, disabled: boolean) => ({
  row: {
    borderBottom: '1px solid #ececec',
    backgroundColor: selected ? '#e6f4fb' : 'transparent'
  },
  cell: {
    padding: '12px 8px',
    fontSize: '13px',
    verticalAlign: 'middle' as const
  },
  cellTime: {
    padding: '12px 8px',
    fontSize: '12px',
    fontWeight: 600 as const,
    verticalAlign: 'middle' as const
  },
  timeContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  cellPrice: {
    padding: '12px 8px',
    textAlign: 'right' as const,
    verticalAlign: 'middle' as const
  },
  priceContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '10px',
    fontWeight: 700 as const,
    color: theme.linkColor,
    fontSize: '15px'
  },
  numberCircle: {
    width: '28px',
    height: '28px',
    minWidth: '28px',
    backgroundColor: theme.linkColor,
    color: '#ffffff',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700 as const,
    fontSize: '12px'
  },
  ticketBadge: {
    backgroundColor: '#e6f4fb',
    color: theme.linkColor,
    padding: '2px 8px',
    borderRadius: '10px',
    fontSize: '11px'
  },
  selectButton: {
    backgroundColor: theme.linkColor,
    color: '#ffffff',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 600 as const,
    cursor: selected || disabled ? 'not-allowed' : 'pointer',
    whiteSpace: 'nowrap' as const,
    opacity: selected || disabled ? 0.5 : 1
  }
});

export default TransportCard;
